import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../../../slices/auth/authenticationSlice";
import { getLmsRole, isParentRole } from "../../utils/lmsRole";
import { getStudents } from "../../students/controllers/studentController";
import { getWeeklyReport } from "../controllers/reportController";
import ReportFilters from "../components/ReportFilters";
import ReportMetricCard from "../components/ReportMetricCard";
import ReportBarChart from "../components/ReportBarChart";
import ReportsEmptyState from "../components/ReportsEmptyState";
import {
    buildMetricCards,
    buildReportCharts,
    formatWeekLabel,
    getReportErrorMessage,
    getWeekStartIso,
    normalizeStudentOption,
    normalizeWeeklyReportResponse,
} from "../utils/reportUtils";
import { ReportsWrapper } from "../styles/ReportsStyle";

const ReportsOverviewPage = () => {
    const dispatch = useDispatch();
    const { credObj } = useSelector((store) => store.auth);
    const { currentUser } = useSelector((store) => store.users);
    const role = getLmsRole({ currentUser, credObj });
    const isParent = isParentRole(role);
    const learnerLabel = isParent ? "Kid" : "Student";
    const learnerPlural = isParent ? "kids" : "students";
    const [students, setStudents] = useState([]);
    const [selectedStudentId, setSelectedStudentId] = useState("");
    const [weekPreset, setWeekPreset] = useState("current");
    const [customWeekDate, setCustomWeekDate] = useState("");

    const [report, setReport] = useState(null);
    const [isLoadingStudents, setIsLoadingStudents] = useState(true);
    const [isLoadingReport, setIsLoadingReport] = useState(false);
    const [error, setError] = useState("");

    const weekStartIso = useMemo(
        () => getWeekStartIso({ preset: weekPreset, customDate: customWeekDate }),
        [weekPreset, customWeekDate]
    );

    const selectedWeekStartDate = useMemo(() => {
        if (weekPreset === "previous") return weekStartIso;
        if (weekPreset === "custom" && customWeekDate) return weekStartIso;
        return undefined;
    }, [weekPreset, customWeekDate, weekStartIso]);

    const handleApiFailure = useCallback(
        (apiError) => {
            if (apiError?.status === 401) {
                dispatch(logoutUser());
                window.location.assign("/lms/login");
                return;
            }

            setError(getReportErrorMessage(apiError));
        },
        [dispatch]
    );

    useEffect(() => {
        let isMounted = true;

        const loadStudents = async () => {
            setIsLoadingStudents(true);
            setError("");
            try {
                const response = await getStudents();
                const items = (response?.data?.students || []).map(normalizeStudentOption);
                if (isMounted) {
                    setStudents(items);
                    if (items.length) {
                        setSelectedStudentId((current) => current || items[0].id);
                    }
                }
            } catch (apiError) {
                if (isMounted) handleApiFailure(apiError);
            } finally {
                if (isMounted) setIsLoadingStudents(false);
            }
        };

        loadStudents();

        return () => {
            isMounted = false;
        };
    }, [handleApiFailure]);

    useEffect(() => {
        if (!selectedStudentId) {
            setReport(null);
            return;
        }

        let isMounted = true;

        const loadReport = async () => {
            setIsLoadingReport(true);
            setError("");
            try {
                const response = await getWeeklyReport({
                    studentId: selectedStudentId,
                    weekStartDate: selectedWeekStartDate,
                });
                if (isMounted) {
                    setReport(normalizeWeeklyReportResponse(response));
                }
            } catch (apiError) {
                if (isMounted) {
                    setReport(null);
                    handleApiFailure(apiError);
                }
            } finally {
                if (isMounted) setIsLoadingReport(false);
            }
        };

        loadReport();

        return () => {
            isMounted = false;
        };
    }, [selectedStudentId, selectedWeekStartDate, handleApiFailure]);

    const metricCards = useMemo(() => buildMetricCards(report), [report]);
    const charts = useMemo(() => buildReportCharts(report), [report]);

    return (
        <ReportsWrapper>
            <section className="reports-header">
                <div>
                    <p className="eyebrow">Reports</p>
                    <h1>Review weekly reading performance</h1>
                    <p>Track progress, celebrate wins, and spot support opportunities quickly.</p>
                </div>
                <p>{`Week of ${formatWeekLabel(weekStartIso)}`}</p>
            </section>

            <ReportFilters
                showStudentFilter
                students={students}
                selectedStudentId={selectedStudentId}
                onStudentChange={setSelectedStudentId}
                weekPreset={weekPreset}
                onWeekPresetChange={setWeekPreset}
                customWeekDate={customWeekDate}
                onCustomWeekDateChange={setCustomWeekDate}
                isLoading={isLoadingStudents || isLoadingReport}
                learnerLabel={learnerLabel}
            />

            {error && (
                <p className="reports-error" role="alert">
                    {error}
                </p>
            )}

            {(isLoadingStudents || isLoadingReport) && <p className="reports-loading">Loading report...</p>}

            {!isLoadingStudents && !isLoadingReport && !error && !report && (
                <ReportsEmptyState learnerPlural={learnerPlural} />
            )}

            {!isLoadingStudents && !isLoadingReport && !error && report && (
                <>
                    <section className="reports-metrics" aria-label="Weekly metric cards">
                        {metricCards.map((metric) => (
                            <ReportMetricCard
                                key={metric.key}
                                metricKey={metric.key}
                                label={metric.label}
                                value={metric.value}
                            />
                        ))}
                    </section>

                    <section className="reports-charts" aria-label="Weekly charts">
                        {charts.map((chart) => (
                            <ReportBarChart
                                key={chart.key}
                                title={chart.title}
                                value={chart.value}
                                max={chart.max}
                                displayValue={chart.displayValue}
                            />
                        ))}
                    </section>

                    <section className="reports-summary" aria-label="Assignment completion summary">
                        <article className="report-summary-card">
                            <h4>Assignments</h4>
                            <p>{`${report.assignmentsCompleted} completed of ${report.assignmentsAssigned} assigned`}</p>
                        </article>
                        <article className="report-summary-card">
                            <h4>Completion Rate</h4>
                            <p>{`${report.completionRate}% completion this week`}</p>
                        </article>
                    </section>
                </>
            )}
        </ReportsWrapper>
    );
};

export default ReportsOverviewPage;
